import { Component, Input } from '@angular/core';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from 'primeng/button';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { PerformancetableDto } from '../../models/dto/performancetable-dto';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { ProgressSpinner } from 'primeng/progressspinner';
import { RouterLink } from '@angular/router';
import { SortEvent } from 'primeng/api';

@Component({
  selector: 'bnpp-indx-home-performance',
  imports: [
    DropdownModule,
    FormsModule,
    ButtonDirective,
    NgClass,
    TableModule,
    NgForOf,
    NgIf,
    PaginatorModule,
    ProgressSpinner,
    RouterLink,
  ],
  templateUrl: './home-performance.component.html',
  styleUrl: './home-performance.component.scss',
  standalone: true,
})
export class HomePerformanceComponent {
  activeTab$: 'cumulative' | 'calendar' = 'cumulative';
  protected first: number = 0;
  protected rows: number = 25;
  defaultSortField = 'technicalName';
  defaultSortOrder = -1;
  protected isFilterApplied = false;
  @Input() totalRecordsCount = 0;
  @Input() set activeTab(tab: 'cumulative' | 'calendar') {
    this.activeTab$ = tab;
    this.setVisibleYears();
    this.setCalendarYears();
  }
  @Input() set performanceTableData(performanceData: PerformancetableDto[]) {
    this.performanceTableData$ = performanceData;
    if (this.totalRecordsCount !== performanceData.length) {
      this.isFilterApplied = true;
    } else {
      this.isFilterApplied = false;
    }
    this.setVisibleYears();
    this.setCalendarYears();
  }
  protected performanceTableData$: PerformancetableDto[] = [];
  visibleYearsStartIndex = 0;

  headerMapping: { [key: string]: keyof PerformancetableDto } = {
    '1M return': 'perf1M',
    '3M return': 'perf3M',
    '6M return': 'perf6M',
    '1Y return': 'perf1Y',
    '3Y return': 'perf3Y',
  };

  visibleHeaders: string[] = [];
  yearsSet = new Set<number>();

  setCalendarYears() {
    if (this.performanceTableData$) {
      this.performanceTableData$.forEach((item) => {
        if (item.performanceYearlyDtoList) {
          Object.values(item.performanceYearlyDtoList).forEach((yearData) => {
            this.yearsSet.add(yearData.Year);
          });
        }
      });
    }
  }

  public exportExcel() {
    const customHeaders = [
      'Index Name',
      'Bloomberg Code',
      'Asset',
      'Style',
      'Geography',
      'Type',
      'Currency',
      'Last Update Date',
      'Last Closing Price',
      'YTD Return',
    ];

    const allHeaders = [...customHeaders, ...this.visibleHeaders];

    const dataWithHeaders = [
      allHeaders,
      ...this.performanceTableData$.map((item) => [
        item.technicalName,
        item.bloombergCode,
        item.asset,
        item.secondLevel,
        item.geography,
        item.type,
        item.mdsCurrencyCode,
        item.formattedLastUpdateDate,
        item.indexLevel,
        item.perfYTD,
        ...this.visibleHeaders.map((header) =>
          this.getVisibleYearData(header, item)
        ),
      ]),
    ];
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(dataWithHeaders);
    // Define the style for the header
    const headerStyle = {
      fill: {
        type: 'pattern', // required – tells ExcelJS we are using a pattern fill
        pattern: 'solid',
        fgColor: { rgb: 'FF0000FF' }, // Blue color
      },
      font: {
        bold: true,
        color: { rgb: 'FFFFFFFF' }, // White text color for better visibility
      },
    };
    const range = XLSX.utils.decode_range(ws['!ref']!);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = headerStyle;
    }
    ws['!autofilter'] = { ref: ws['!ref']! };
    const wb: XLSX.WorkBook = {
      Sheets: { Performance: ws },
      SheetNames: ['Performance'],
    };
    const excelBuffer: ArrayBuffer = XLSX.write(wb, {
      bookType: 'xlsx',
      type: 'array',
    });
    const fileName = 'PerformanceData_' + this.activeTab$ + '.xlsx';
    saveAs(new Blob([excelBuffer], { type: 'application/octet' }), fileName);
  }

  setVisibleYears() {
    if (this.activeTab$ === 'cumulative') {
      this.visibleHeaders = [
        '1M return',
        '3M return',
        '6M return',
        '1Y return',
        '3Y return',
      ];
    } else {
      this.visibleHeaders = Array.from(this.yearsSet)
        .sort((a, b) => b - a)
        .slice(this.visibleYearsStartIndex, this.visibleYearsStartIndex + 5)
        .map((year) => year.toString());
    }
  }

  getVisibleYearHeaderKey(year: string) {
    if (this.activeTab$ === 'cumulative') {
      return this.headerMapping[year];
    } else {
      return year;
    }
  }

  scrolllVisibleYears(count: number) {
    this.visibleYearsStartIndex += count;
    this.setVisibleYears();
  }

  getReturnClass(val: number, isLast?: boolean): string {
    let cssClass = '';
    if (val >= 0) cssClass = 'text-green-600 font-semibold';
    else if (val < 0) cssClass = 'text-red-600 font-semibold';
    else cssClass = 'text-gray-500';

    cssClass += isLast ? 'w-[9%]' : 'w-[14%]';
    return cssClass;
  }

  getVisibleYearData(header: string, performance: PerformancetableDto) {
    if (this.activeTab$ === 'cumulative') {
      const field = this.headerMapping[header];
      if (field) {
        const value = performance[field];
        return typeof value === 'number' ? value : null;
      } else {
        return null;
      }
    } else {
      const year = parseInt(header, 10);
      if (!isNaN(year) && performance.performanceYearlyDtoList) {
        const yearlyData = performance.performanceYearlyDtoList.find(
          (item) => item.Year === year
        );
        return yearlyData ? yearlyData.Value : null;
      } else {
        return null;
      }
    }
  }

  onSortChange(event: SortEvent) {
    if (event.field && event.order && this.activeTab$ === 'calendar') {
      const sortedData = [...this.performanceTableData$].sort((a, b) => {
        const adata = a.performanceYearlyDtoList
          ? a.performanceYearlyDtoList.find(
              (item) => item.Year === parseInt(event.field || '', 10)
            )
          : null;
        const bdata = b.performanceYearlyDtoList
          ? b.performanceYearlyDtoList.find(
              (item) => item.Year === parseInt(event.field || '', 10)
            )
          : null;
        const valueA = adata ? adata.Value : 0;
        // const valueA = a[event.field];
        const valueB = bdata ? bdata.Value : 0;

        return (valueA < valueB ? -1 : 1) * (event.order || 0);
      });
      this.performanceTableData$ = sortedData;
    }
  }
}
